import { useMasters } from "~/store/masters";
import { useCalendar } from "~/store/calendar";
import { useUserStore } from "~/store/auth";
import { getDefaultWorkTime } from "~/composables/crews";
import { computed, isRef } from "vue";
import WWSS from "~/utils/wwss";

import { useGlobalScheduling } from "~/store/globalScheduling";

import { sortByDate } from "~/composables/general";

const normalizeCrewId = (crewId) => {
  return crewId === null || crewId === undefined || crewId === ""
    ? CREW_NULL
    : crewId;
};

const isWithoutCrewWorkorder = (workorder) => {
  return normalizeCrewId(workorder?.AMCREW) === CREW_NULL;
};

// por tipos
export const woTypesColors = {
  CORRDFR: "grey-lighten-2", //Correctivo programado
  CORRINM: "deep-orange-lighten-3", //Correctivo inmediato
  DISCORR: "teal-accent-3", //correctivo tecnologicas(modificaciones)
  DISMEJ: "green-lighten-2", //mejoras tecnologicas (modificaciones)
  DISENS: "teal-lighten-3", //Maniobras
  INSPNT: "yellow-accent-3", //inspecciones específicas
  INSPRD: "lime-accent-1", //inspecciones periodicas
  MNBAMT: "light-green-lighten-1", // Maniobra Alta/ Media Tension
  OTRMAN: "lime-lighten-1", //Otras manipulaciones
  PRVPRD: "pink-lighten-4", // Predictivo
  PRVSIS: "amber-lighten-4", //Preventivo sistematica
  RESLOC: "blue-lighten-4", // Reserva localización
  DEFAULT: "cyan-lighten-5", //Defecto
  ALERT: "deep-orange-darken-4", // Duración <= 1
};

export const woTypesBorders = {
  CORRDFR: "grey-darken-2", //Correctivo programado
  CORRINM: "deep-orange-darken-1", //Correctivo inmediato
  DISCORR: "teal-darken-2", //correctivo tecnologicas(modificaciones)
  DISMEJ: "green-darken-2", //mejoras tecnologicas (modificaciones)
  DISENS: "teal-darken-1", //Maniobras
  INSPNT: "yellow-darken-2", //inspecciones específicas
  INSPRD: "lime-darken-1", //inspecciones periodicas
  MNBAMT: "light-green-darken-3", // Maniobra Alta/ Media Tension
  OTRMAN: "lime-darken-3", //Otras manipulaciones
  PRVPRD: "pink-darken-1", // Predictivo
  PRVSIS: "amber-darken-1", //Preventivo sistematica
  RESLOC: "blue-darken-2", // Reserva localización
  DEFAULT: "cyan-lighten-1", //Defecto
  ALERT: "deep-orange-darken-4", // Duración <= 1
};

export const stripLeadingZeros = (value) => String(value).replace(/^0+/, "");

/**
 * Función que adapta la salida de las OTs a estructura de cuadrillas/OTs para ser pintadas en el calendario
 * @param {Array} wos_data
 * @returns {Array} crews_data
 */
export const parseAlgorithmWOToCrews = (wos_data) => {
  const masters = useMasters();
  const calendar = useCalendar();
  const globalScheduling = useGlobalScheduling();
  const crews_data = [];

  const sorted_ots = sortAlgorithmWorkorders(wos_data);

  sorted_ots.forEach((ot) => {
    let crew = crews_data.find((crew_data) => crew_data.id === ot.OPT_CREW);
    const ot_wonumId = ot.COD_WONUM_ID.split("-")[1];
    const siteid = ot.COD_WONUM_ID.split("-")[0];

    if (
      globalScheduling.algorithmLastExecution == null ||
      parseDateAsUTC(globalScheduling.algorithmLastExecution) <
        parseDateAsUTC(ot.OPT_EXECUTION_DATE)
    ) {
      globalScheduling.algorithmLastExecution = ot.OPT_EXECUTION_DATE;
    }

    if (!crew) {
      const crews = masters.getAllCrews;
      const crewInfo = crews.find((c) => c.AMCREWID == ot.OPT_CREW);
      crew = {
        id: ot.OPT_CREW,
        name: crewInfo?.AMCREW ?? "",
        disabled: [],
        ots: [],
        description: crewInfo?.DESCRIPTION ?? "",
        personGroup: crewInfo?.PERSONGROUP ?? "",
        hours: crewInfo?.HOURS ?? "",
        person_num: ot?.OPT_NWORKERS ?? 0,
        type: crewInfo?.AMCREWTYPE ?? "",
        real: true,
        sites: getCrewSitesNames(crewInfo),
        slots: Array.from({ length: 7 }, (_, i) => {
          return {
            AMCREWAVAILAEID: "",
            OPDATE: calendar.getOnlyWeekDays[i],
            AMCREWID: "",
            available: false,
            workers: [],
          };
        }),
        workers: Array.from({ length: 7 }, () => []), //Inicializo un array de arrays para los por semana
      };
      crews_data.push(crew);
      if (!globalScheduling.showCrewsData[ot.OPT_CREW]) {
        globalScheduling.showCrewsData[ot.OPT_CREW] = {
          planning: false,
          algorithm: false,
        };
      }
    }

    const existingOT = crew.ots.find(
      (wo) =>
        wo.i === ot_wonumId &&
        ot.subcontractor === ot.SUBCONTRACTOR &&
        wo.siteid === siteid
    );

    const day_index = getDateDay(parseDateAsUTC(ot.OPT_SCHEDSTART));
    crew.slots[day_index].available = true;

    if (!existingOT) {
      crew.ots.push({
        actualSchedStart: parseDateAsUTC(ot.OPT_SCHEDSTART),
        color: woTypesColors[ot.COD_WORKTYPE] ?? woTypesColors["DEFAULT"],
        borderColor:
          woTypesBorders[ot.COD_WORKTYPE] ?? woTypesBorders["DEFAULT"],
        crewId: ot.OPT_CREW,
        description: ot.DES_DESCRIPTION,
        disabled: [0, 6],
        duration: String(ot.MET_ESTDUR),
        durationText: parseHoursToText(parseFloat(ot.MET_ESTDUR)),
        enabledDays: [getDateDay(parseDateAsUTC(ot.OPT_SCHEDSTART))],
        end: parseDateAsUTC(ot.OPT_SCHEDSTART),
        endsAfterWeek: isAfterWeek(ot.OPT_OT_ENDDATE, ot.OPT_SCHEDSTART),
        h: 1,
        i: ot_wonumId,
        isDragging: false,
        location: masters.getLocationCodeAndName(ot.COD_LOCATION_ID),
        locationId: ot.COD_LOCATION_ID,
        locationName: masters.getLocationName(ot.COD_LOCATION_ID),
        wonum: ot_wonumId,
        regular: ot.FLG_NOTREGULAR,
        schedStart: parseDateAsUTC(ot.OPT_SCHEDSTART),
        start: parseDateAsUTC(ot.OPT_SCHEDSTART),
        startsBeforeWeek: isBeforeWeek(ot.OPT_OT_STARTDATE, ot.OPT_SCHEDSTART),
        status: null,
        subcontractor: ot.SUBCONTRACTOR,
        siteid,
        timeWorked: "0",
        tieneOTsHijas: ot.TIENEOTSHIJAS ?? "0",
        w: 1,
        x: getDateDay(parseDateAsUTC(ot.OPT_SCHEDSTART)),
        y: crew.ots.length,
      });
    } else {
      existingOT.enabledDays.push(
        getDateDay(parseDateAsUTC(ot.OPT_SCHEDSTART))
      );
      const start_day = Math.min(...existingOT.enabledDays);
      const end_day = Math.max(...existingOT.enabledDays);
      existingOT.w = Math.max(end_day - start_day, 1) + 1;
      existingOT.x = start_day;
      //   Si la ot que se itera es la misma pero comienza antes
      if (parseDateAsUTC(ot.OPT_SCHEDSTART) < existingOT.schedStart) {
        existingOT.actualSchedStart = parseDateAsUTC(ot.OPT_SCHEDSTART);
        existingOT.start = parseDateAsUTC(ot.OPT_SCHEDSTART);
        existingOT.schedStart = parseDateAsUTC(ot.OPT_SCHEDSTART);
      } else {
        existingOT.end = parseDateAsUTC(ot.OPT_SCHEDSTART);
      }
    }

    let workers_crew_index = getDateDay(parseDateAsUTC(ot.OPT_SCHEDSTART));

    const workers = (
      ot.OPT_TRABAJADORES?.slice(1, -1).replace(/NULL/g, "") || ""
    )
      .split(",")
      .map((worker) => worker.trim())
      .filter((worker) => worker !== "");
    crew.slots[workers_crew_index].workers = [
      ...new Set(crew.slots[workers_crew_index].workers.concat(workers)),
    ];
  });

  return sortElements(crews_data, "description");
};

export const parseCalendarWOToCrews = (ots) => {
  const masters = useMasters();
  const calendar = useCalendar();
  const globalScheduling = useGlobalScheduling();
  const nuxtApp = useNuxtApp();

  let crews_with_wos = [];
  let wos_without_crews = [];
  let crews_without_wos = [];

  masters.getAllCrews.forEach((crew) => {
    const crew_data = {
      id: crew.AMCREW,
      name: crew.AMCREW,
      disabled: [],
      ots: [],
      unplanned_ots: [],
      description: crew.DESCRIPTION || "",
      personGroup: crew.PERSONGROUP || "",
      hours: crew.HOURS || "",
      person_num: crew.PERSONNUM || 0,
      type: crew.AMCREWTYPE || "",
      orgid: crew.ORGID || "TEST",
      real: true,
      sites: getCrewSitesNames(crew),
      slots: Array.from({ length: 7 }, (_, i) => {
        return {
          AMCREWAVAILAEID: "",
          OPDATE: calendar.getOnlyWeekDays[i],
          AMCREWID: "",
          available: false,
          workers: [],
        };
      }), //Inicia array de valor false para indicar si hay slot (cuadrilla asociada en el día)
      workers: Array.from({ length: 7 }, () => []), //Inicializo un array de arrays para los por semana
    };

    // Slots
    const crew_slots = globalScheduling.crewsSlots[crew.AMCREW];

    if (crew_slots !== undefined && crew_slots.length) {
      crew_slots.forEach((slot) => {
        const day_index = getDateDay(parseDateAsUTC(slot.OPDATE));

        crew_data.slots[day_index].available = true;
        crew_data.slots[day_index].AMCREWAVAILAEID = slot.AMCREWAVAILAEID;
        crew_data.slots[day_index].AMCREWID = slot.AMCREWID;
        crew_data.slots[day_index].OPDATE = slot.OPDATE;
      });
    }

    // OTs
    const crew_workorders = ots.filter((wo_data) => {
      return wo_data.AMCREW === crew.AMCREW;
    });

    const { wo, unplanned_wo } = parsePlanningOTs(crew_workorders, crew_data);

    crew_data.ots = wo;
    crew_data.unplanned_ots = unplanned_wo;

    if (crew_data.ots.length || crew_data.unplanned_ots.length) {
      crews_with_wos.push(crew_data);
    } else {
      crews_without_wos.push(crew_data);
    }
  });

  crews_with_wos = sortElements(crews_with_wos, "description");
  crews_without_wos = sortElements(crews_without_wos, "description");

  const workorders_null = ots.filter((wo) => {
    return isWithoutCrewWorkorder(wo);
  });

  if (workorders_null.length) {
    const not_crew_data = {
      id: CREW_NULL,
      name: nuxtApp.$i18n.t("scheduling.withoutCrewId"),
      description: nuxtApp.$i18n.t("scheduling.withoutCrew"),
      disabled: [],
      ots: [],
      unplanned_ots: [],
      hours: DEFAULT_CREW_HOURS,
      person_num: DEFAULT_CREW_WORKERS,
      type: "",
      orgid: "",
      real: false,
      slots: createWithoutCrewSlots(), //No hay slots porque son OTs sin cuadrila
      workers: Array.from({ length: 7 }, () => []), //Inicializo un array de arrays para los por semana
    };
    const { wo, unplanned_wo } = parsePlanningOTs(
      workorders_null,
      not_crew_data
    );
    not_crew_data.ots = wo;
    not_crew_data.unplanned_ots = unplanned_wo;
    wos_without_crews.push(not_crew_data);
  }

  // Si hay filtros aplicados, filtramos las cuadrillas si hay filtros aplicados
  const filtered = countAppliedFilters(globalScheduling.filters) > 0;

  const wosWithoutCrewsFiltered = filtered
    ? wos_without_crews.filter(hasOtsOrUnplannedOts)
    : wos_without_crews;
  const crewsWithWosFiltered = filtered
    ? crews_with_wos.filter(hasOtsOrUnplannedOts)
    : crews_with_wos;
  const crewsWithoutWosFiltered = filtered
    ? crews_without_wos.filter(hasOtsOrUnplannedOts)
    : crews_without_wos;
  return [
    ...wosWithoutCrewsFiltered,
    ...crewsWithWosFiltered,
    ...crewsWithoutWosFiltered,
  ];
};

const createWithoutCrewSlots = () => {
  const calendar = useCalendar();
  let slots = [];

  for (let i = 0; i < 7; i++) {
    const date_day = addDays(calendar.getOnlyWeekDays[0], i);
    const holiday = i >= 5;

    slots.push({
      available: !holiday,
      AMCREWAVAILAEID: "",
      AMCREWID: "",
      OPDATE: formatDate(date_day, "iso"),
    });
  }

  return slots;
};

// Función para verificar si un elemento tiene ots o unplanned_ots con valores
const hasOtsOrUnplannedOts = (element) => {
  return element.ots.length || element.unplanned_ots.length;
};

export const parsePlanningOTs = (ots, crew_data) => {

  if (ots.length === 0) {
    return { wo: [], unplanned_wo: [] };
  }

  let positionY = 0;
  const crew_workorders = [];
  const crew_unplanned_wo = [];

  const sorted_ots = sortPlanningWorkorders(ots);

  sorted_ots.forEach((wo) => {
    positionY = 0;

    const workorderWeekPlanlabor = getWorkorderWeekPlanlabor(wo.WORKORDERID);
    const start_date = generateStartDate(wo, workorderWeekPlanlabor);

    if (start_date == null) {
      crew_unplanned_wo.push(createWOWihtoutSlot(wo));
      return;
    }

    crew_workorders.push(
      createPlannedWO(
        wo,
        crew_data,
        start_date,
        positionY,
        workorderWeekPlanlabor
      )
    );

    positionY += 1;
  });

  return {
    wo: crew_workorders,
    unplanned_wo: crew_unplanned_wo,
  };
};

/**
 * Obtiene la planificación de mano de obra (planlabor) de la OT en la semana de navegación
 * @param {String} workorderid
 * @returns
 */
const getWorkorderWeekPlanlabor = (workorderid) => {
  const globalScheduling = useGlobalScheduling();
  const hasPlanlabor = globalScheduling.workorderHasPlanlabor(workorderid);
  if (!hasPlanlabor) {
    return null;
  }
  return globalScheduling.planlaborPlanning.filter(
    (planlabor) =>
      planlabor.WORKORDERID === workorderid &&
      globalScheduling.planlaborInNavigationWeek(planlabor.SCHEDDATE)
  );
};

/**
 * Obtiene la planificación de mano de obra (planlabor) de la OT en las semanas anteriores a la de navegación
 * @param {String} workorderid
 * @returns
 */
const getWorkorderBeforeWeekPlanlabor = (workorderid) => {
  const globalScheduling = useGlobalScheduling();
  const hasPlanlabor = globalScheduling.workorderHasPlanlabor(workorderid);
  if (!hasPlanlabor) {
    return null;
  }
  return globalScheduling.planlaborPlanning.filter(
    (planlabor) =>
      planlabor.WORKORDERID === workorderid &&
      globalScheduling.planlaborBeforeNavigationWeek(planlabor.SCHEDDATE)
  );
};

/**
 * Obtiene la planificación de mano de obra (planlabor) de la OT en las semanas posteriores a la de navegación
 * @param {String} workorderid
 * @returns
 */
const getWorkorderAfterWeekPlanlabor = (workorderid) => {
  const globalScheduling = useGlobalScheduling();
  const hasPlanlabor = globalScheduling.workorderHasPlanlabor(workorderid);
  if (!hasPlanlabor) {
    return null;
  }
  return globalScheduling.planlaborPlanning.filter(
    (planlabor) =>
      planlabor.WORKORDERID === workorderid &&
      globalScheduling.planlaborAfterNavigationWeek(planlabor.SCHEDDATE)
  );
};

/**
 * Retorna los totales de trabajo planificado fuera de la semana como una propiedad computada.
 * * @param {string} workorderid El ID de la orden de trabajo.
 */
export const useWorkorderPlanlaborMap = (workorderIds) => {
  const globalScheduling = useGlobalScheduling();

  // Asegúrate de que workorderIds sea accesible, incluso si no es una Ref
  const ids = isRef(workorderIds) ? workorderIds : computed(() => workorderIds);

  const planlaborMap = computed(() => {
    const map = {};

    // 1. Iteramos sobre la lista de IDs (que es reactiva)
    for (const workorderid of ids.value) {
      let beforeWeekTotal = 0;
      let afterWeekTotal = 0;

      // Accedemos a los datos reactivos de la store
      const workorderHasPlanlabor =
        globalScheduling.workorderHasPlanlabor(workorderid);

      if (workorderHasPlanlabor) {
        const beforeWeekData = getWorkorderBeforeWeekPlanlabor(workorderid);
        const afterWeekData = getWorkorderAfterWeekPlanlabor(workorderid);

        const filteredBeforeWeekData = beforeWeekData?.filter(
          (item) => !isBeforeToday(parseDateAsUTC(item.SCHEDDATE))
        );

        // 2. Calculamos los totales
        beforeWeekTotal =
          filteredBeforeWeekData?.reduce(
            (acc, curr) => acc + Number(curr.LABORHRS),
            0
          ) || 0;

        afterWeekTotal =
          afterWeekData?.reduce(
            (acc, curr) => acc + Number(curr.LABORHRS),
            0
          ) || 0;
      }

      // 3. Almacenamos el resultado en el mapa, usando el ID como clave
      map[workorderid] = {
        beforeWeek: beforeWeekTotal,
        afterWeek: afterWeekTotal,
        totalOutsideWeek: beforeWeekTotal + afterWeekTotal,
      };
    }

    // El mapa completo se recalcula si la lista de IDs o cualquier dato de globalScheduling cambia.
    return map;
  });

  return {
    planlaborMap, // Este es el objeto computado que contiene todos los cálculos.
  };
};

/**
 * Establece la estrategía apra calcular el día de inicio de la OT
 * @param {String} workorder
 * @param {Array | null} weekPlanlabor
 */
const generateStartDate = (workorder, weekPlanlabor) => {
  return Array.isArray(weekPlanlabor) && !!weekPlanlabor.length > 0
    ? generateStartDatePlanlabor(workorder.FECHA_PROGRAMADA, weekPlanlabor)
    : generateStartDateCrew(workorder.AMCREW, workorder.FECHA_PROGRAMADA);
};

/*   Buscar si la cuadrilla asociada a la OT tiene "slot" para ese día de fecha_programada	
	Sí SÍ que tiene slot -> la OT empieza a trabajarse ese día
	Si NO tiene slot -> La OT empieza a trabajarse en el siguiente día con slot de la cuadrilla */
const generateStartDateCrew = (AMCREW, scheduledDate) => {
  const crews = useGlobalScheduling().crewsSlots;

  let start_date =
    compareDateWithToday(parseDateAsUTC(scheduledDate), "<") &&
    isCurrentWeek(scheduledDate)
      ? formatDate(parseDateAsUTC(), "iso")
      : scheduledDate;

  const crew_id = normalizeCrewId(AMCREW);

  //Si no hay Slots de la cuadrilla se devuelve null para mostrar como unplanned_ot
  if (!crews[crew_id]) {
    return null;
  }

  let date = null;

  crews[crew_id].forEach((slot) => {
    if (slot.OPDATE == start_date) {
      date = start_date;
    }

    if (
      date == null &&
      parseDateAsUTC(slot.OPDATE) > parseDateAsUTC(start_date)
    ) {
      date = slot.OPDATE;
    }
  });

  return date;
};

const generateStartDatePlanlabor = (startDate, weekPlanlabor) => {
  if (!weekPlanlabor.length) {
    return startDate;
  }

  const earlyDate = weekPlanlabor.reduce((earliest, current) => {
    const currentDate = parseDateAsUTC(current.SCHEDDATE);
    const earliestDate = parseDateAsUTC(earliest.SCHEDDATE);
    return currentDate < earliestDate ? current : earliest;
  });

  // Formatear la fecha al final
  return formatDate(parseDateAsUTC(earlyDate.SCHEDDATE), "iso");
};

/**
 *
 * @param {Object} workorder
 * @param {String} startDate  Formato "Iso"
 * @param {Number} duration
 * @param {Object} crewData
 * @param {Array | null} weekPlanlabor
 * @returns
 */
const generateEndDate = (
  workorder,
  startDate,
  duration,
  crewData,
  weekPlanlabor
) => {
  return Array.isArray(weekPlanlabor) && weekPlanlabor.length > 0
    ? generateEndDatePlanlabor(workorder, startDate, duration, weekPlanlabor)
    : generateEndDateCrew(workorder.AMCREW, startDate, duration, crewData);
};

/**
 *
 * @param {String} AMCREW
 * @param {String} startDate  Formato "Iso"
 * @param {Number} duration
 * @param {Object} crewData
 * @returns
 */
const generateEndDateCrew = (AMCREW, startDate, duration, crewData) => {
  const calendar = useCalendar();
  const masters = useMasters();
  const globalScheduling = useGlobalScheduling();
  const crews = globalScheduling.crewsSlots;

  let dias = 0;

  let horasRestantes = duration;
  let endDate = "";
  const enabledDays = [];
  const workedRemainingHours = {};
  const workedHours = {};

  if (duration <= 0) {
    enabledDays.push(getDateDay(parseDateAsUTC(startDate)));
  }

  const amcrew_id = normalizeCrewId(AMCREW);

  if (!crews[amcrew_id]) {
    const horasDisponibles = getDefaultWorkTime();

    while (horasRestantes > 0) {
      const daySum = addDays(parseDateAsUTC(startDate), dias);
      const day_week = getDateDay(daySum);
      const holiday = [5, 6].includes(day_week);

      if (holiday) {
        break;
      }

      if (horasRestantes > horasDisponibles) {
        dias++;
      }
      horasRestantes -= horasDisponibles;

      if (daySum <= calendar.currentWeekEndDate) {
        enabledDays.push(getDateDay(daySum));
        workedRemainingHours[getDateDay(daySum)] = horasRestantes;
        workedHours[getDateDay(daySum)] = horasDisponibles;
      }
    }
  } else {
    crews[amcrew_id].forEach((slot) => {
      // Obtiene la info original de la cuadrilla

      const crew_data = masters.getAllCrewsAMCREW[amcrew_id];

      // Los trabajadores disponibles en el slot. Se comprueba si son de OTs sin cuadrilla para asignar el valor por defecto
      const assignedWorkers =
        amcrew_id != CREW_NULL
          ? parseInt(crew_data.PERSONNUM)
          : DEFAULT_CREW_WORKERS;
      const work_hours =
        amcrew_id != CREW_NULL
          ? parseFloat(crewData.hours)
          : DEFAULT_CREW_HOURS;

      // Si hay trabajadores en el slot y la fecha del slot es igual o posterior a la del slot y quedan horas pendiente de completar
      if (
        assignedWorkers != 0 &&
        parseDateAsUTC(slot.OPDATE) >= parseDateAsUTC(startDate) &&
        horasRestantes > 0
      ) {
        // Si los días es menor que lo que queda de semana
        if (
          getDateDay(parseDateAsUTC(slot.OPDATE)) + dias <=
            getDateDay(calendar.currentWeekEndDate) &&
          assignedWorkers
        ) {
          // Se añade el día del slot como habilitad
          enabledDays.push(getDateDay(parseDateAsUTC(slot.OPDATE)));

          // Se calculan las horas de la cuadrilla por el número de técnicos del slot
          let horasDisponibles = work_hours * assignedWorkers;

          // A las horas restantes se les restan las horas trabajadas del slot
          horasRestantes -= horasDisponibles;

          // Se añden las horas pendientes de trabajar y las horas trabajables a la cuadrilla
          workedRemainingHours[getDateDay(parseDateAsUTC(slot.OPDATE))] =
            horasRestantes;
          workedHours[getDateDay(parseDateAsUTC(slot.OPDATE))] =
            horasDisponibles;
        }
      }
    });
  }

  // Si sobran horas significa que todavía me faltan días de trabajo pongo que termina la prox semana
  if (horasRestantes > 0) {
    endDate = getDateYMD(
      addDays(parseDateAsUTC(calendar.currentWeekEndDate), 1)
    );
  } else {
    const minDay =
      Math.min(...enabledDays) !== Infinity ? Math.min(...enabledDays) : 0;
    const maxDay =
      Math.max(...enabledDays) !== -Infinity ? Math.max(...enabledDays) : 0;
    const days = maxDay - minDay;
    endDate = getDateYMD(addDays(parseDateAsUTC(startDate), days));
  }

  return {
    end: endDate,
    enabledDays,
    workedRemainingHours,
    workedHours,
  };
};

/**
 * Calcula la fecha de fin de la OT en base a la planificación de técnicos (planlabor)
 * @param {Object} workorder
 * @param {String} startDate  Formto "Iso"
 * @param {Number} duration
 * @param {Array \ null} weekPlanlabor Info de la MOP de la OT en la semana de navegación
 * @returns
 */
const generateEndDatePlanlabor = (
  workorder,
  startDate,
  duration,
  weekPlanlabor
) => {
  const calendar = useCalendar();
  // let horasRestantes = duration;
  let endDate = workorder.FECHA_PROGRAMADA;

  const workedRemainingHours = {};
  const workedHours = {};

  // 🧱 Caso 1: No hay mano de obra planificada en la semana
  if (!weekPlanlabor || weekPlanlabor.length === 0) {
    return {
      end: endDate,
      enabledDays: [getDateDay(parseDateAsUTC(startDate))],
      workedRemainingHours,
      workedHours,
    };
  }

  // Ordenar por fecha
  weekPlanlabor = sortByDate(weekPlanlabor, "SCHEDDATE");

  // Calcular total de horas planificadas
  const totalPlanHours = weekPlanlabor.reduce(
    (sum, p) => sum + (Number(p.LABORHRS) ?? 0),
    0
  );

  // Caso 2 y 3: plan con 0 horas y duración también 0
  if (duration <= 0) {
    // Si no queda duración restante, no hay que consumir horas

    const firstDay = parseDateAsUTC(weekPlanlabor[0].SCHEDDATE);
    const endDate =
      totalPlanHours > 0
        ? getDateYMD(firstDay, 1)
        : getDateYMD(addDays(parseDateAsUTC(calendar.currentWeekEndDate), 1));

    return {
      end: endDate, // de esta manera planteamos que la OT continua la semana que viene porque no se consumen horas
      enabledDays: [getDateDay(firstDay)],
      workedRemainingHours,
      workedHours,
    };
  }

  const enabledDaysSet = new Set();
  weekPlanlabor
    .sort((a, b) => parseDateAsUTC(a.SCHEDDATE) - parseDateAsUTC(b.SCHEDDATE))
    .forEach((plan) => {
      const dia = getDateDay(parseDateAsUTC(plan.SCHEDDATE));
      const horasPlanificadas = parseFloat(plan.LABORHRS) || 0;

      // Si no hay horas planificadas, no se trabaja ese día
      if (horasPlanificadas <= 0) return;

      // Marcamos el día como trabajado
      enabledDaysSet.add(dia);

      // Guardamos las horas planificadas tal cual
      workedHours[dia] = (workedHours[dia] || 0) + horasPlanificadas;
      if (
        normalizeDate(parseDateAsUTC(endDate)) <
        normalizeDate(parseDateAsUTC(plan.SCHEDDATE))
      ) {
        endDate = plan.SCHEDDATE;
      }
    });

  const planlaborAfterWeek = getWorkorderAfterWeekPlanlabor(
    workorder.WORKORDERID
  );
  if (planlaborAfterWeek.length) {
    endDate = getDateYMD(planlaborAfterWeek[0].SCHEDDATE);
  }
  return {
    end: endDate,
    enabledDays: [...enabledDaysSet],
    workedRemainingHours,
    workedHours,
  };
};

export const cleanAllSchedulingFilters = (filter_scheduling = true) => {
  const globalScheduling = useGlobalScheduling();

  globalScheduling.resetGeneralListFilters();

  // comunes
  globalScheduling.filters.wo_number = "";
  globalScheduling.filters.wo_description = "";
  globalScheduling.filters.worktype = [];
  globalScheduling.filters.crew = [];
  globalScheduling.filters.group = [];
  globalScheduling.filters.site = [];
  globalScheduling.filters.asset = [];
  globalScheduling.filters.location = [];
  globalScheduling.filters.subcontractor = [];
  globalScheduling.filters.planlabor = [];
  globalScheduling.filters.gama = [];
  globalScheduling.filters.regular = "";
  // Planificado
  globalScheduling.filters.wo_mother_number = "";
  globalScheduling.filters.technology = [];
  globalScheduling.filters.location_type = [];
  globalScheduling.filters.reprogrammable = false;
  globalScheduling.filters.qsew = false;

  // Eliminar filtro aplicado (como string en vez de como null para prevenir filtrado por defecto automático)
  globalScheduling.filterApplied = "";
  globalScheduling.setPendingFilters(false);

  if (filter_scheduling) {
    filterScheduling();
  }
};

export const filterScheduling = () => {
  const globalScheduling = useGlobalScheduling();
  globalScheduling.setFilteredAlgorithmWorkorders();
  globalScheduling.setFilteredPlanningWorkorders();
  globalScheduling.setFilteredGeneralWorkorders();
  globalScheduling.setPendingFilters(false);
};

export const getCrewSitesNames = (crew) => {
  if (!crew) return [];

  const masters = useMasters();
  const crew_sites_amcrew = crew.SITEID_AMCREWSITEIDID.split(",");

  const sites = [];

  crew_sites_amcrew.forEach((site_amcrew) => {
    const site = site_amcrew.split("_")[0];
    let site_name = `${site}`;
    if (masters.machines.sites[site]) {
      site_name = site_name + ` · ${masters.machines.sites[site]?.SITEDESC}`;
    }

    sites.push(site_name);
  });

  return sites;
};

export const getRemainingDuration = (totalDuration, timeWorked) => {
  let remaining_duration = parseFloat(totalDuration) - parseFloat(timeWorked);

  if (remaining_duration <= 0) {
    return null;
  }

  return remaining_duration;
};

// Función para crear órdenes no planificadas
const createWOWihtoutSlot = (ot) => {
  const masters = useMasters();

  const start_date =
    parseDateAsUTC(ot.FECHA_PROGRAMADA) < parseDateAsUTC()
      ? formatDate(parseDateAsUTC(), "iso")
      : ot.FECHA_PROGRAMADA;

  return {
    actualSchedStart: parseDateAsUTC(ot.FECHA_PROGRAMADA),
    binmea: ot.BINMEA,
    claseTrabajo: ot.CLASE_TRABAJO,
    color: woTypesColors[ot.CLASE_TRABAJO] ?? woTypesColors["DEFAULT"],
    borderColor: woTypesBorders[ot.CLASE_TRABAJO] ?? woTypesBorders["DEFAULT"],
    crewId: normalizeCrewId(ot.AMCREW),
    description: ot.DESCRIPTION,
    disabled: [],
    duration: ot.DURACION,
    durationText: parseHoursToText(parseFloat(ot.DURACION)),
    enabledDays: [],
    end: start_date,
    endsAfterWeek: false,
    entrega: ot.ENTREGA,
    expirationDate: ot.CADUCIDAD,
    limitDate: ot.LIMITDATE,

    h: 0,
    i: ot.WONUM,
    internalHours: ot.WORKORDEREXTRA?.INTERNAL_HOURS ?? ot.INTERNAL_HOURS,
    isDragging: false,
    location: masters.getLocationCodeAndName(ot.SITEID + "-" + ot.LOCATION),
    locationId: ot.LOCATION,

    locationName: masters.getLocationName(ot.SITEID + "-" + ot.LOCATION),
    matPlanificados: ot.MATPLANIFICADOS,
    mother_wo_description: ot.DESCOTMADRE,
    motherWonum: ot.OTMADRE,
    orgid: ot.ORGID,
    otAReprogramar: ot.OTAREPROGRAMAR,
    personGroup: ot.PERSONGROUP,
    criticality: ot.CRITICIDAD,
    legal: ot.LEGAL,
    supplyChainDate: ot.NEWREQUIREDATE,
    targetDate: ot.PMNEXTDATE,
    windMax: ot.WINDMAX,
    windMin: ot.WINDMIN,
    reliabilityDegree: ot.RELIABILITYDEGREE,

    qsew: ot.QSEW,
    regular: ot.FLG_NOTREGULAR,
    schedStart: parseDateAsUTC(start_date),
    semaforo: ot.SEMAFORO,
    siteid: ot.SITEID,

    start: parseDateAsUTC(start_date),
    startsBeforeWeek: false,
    status: ot.STATUS,
    subcontractor: "",
    technicians: ot.PLANLABOR ? ot.PLANLABOR.split("#") : [],
    timeWorked: ot.ACTLABHRS,
    tieneOTsHijas: ot.TIENEOTSHIJAS,
    w: 0,
    wonum: ot.WONUM,
    workedHours: null,
    workedRemainingHours: null,
    workorderid: ot.WORKORDERID,
    x: 0,
    y: 0,
  };
};

// Función para crear órdenes planificadas
/**
 *
 * @param {Object} workorder
 * @param {Object} crew_data
 * @param {String} start_date En formato "iso"
 * @param {Number} positionY
 * @param {Array | null} workorderWeekPlanlabor
 * @returns
 */
const createPlannedWO = (
  workorder,
  crew_data,
  start_date,
  positionY,
  workorderWeekPlanlabor
) => {
  const masters = useMasters();

  const start = start_date;
  const { end, enabledDays, workedRemainingHours, workedHours } =
    generateEndDate(
      workorder,
      start_date,
      workorder.DURACION - workorder.ACTLABHRS,
      crew_data,
      workorderWeekPlanlabor
    );

  const start_day = Math.min(...enabledDays);
  const end_day = Math.max(...enabledDays);
  const endTmp = parseDateAsUTC(end).setHours(23, 59, 59, 999);
  const size = end_day == start_day ? 1 : Math.max(end_day - start_day, 1) + 1;

  return {
    actualSchedStart: parseDateAsUTC(workorder.FECHA_PROGRAMADA),
    binmea: workorder.BINMEA,
    claseTrabajo: workorder.CLASE_TRABAJO,
    color: woTypesColors[workorder.CLASE_TRABAJO] ?? woTypesColors["DEFAULT"],
    borderColor:
      woTypesBorders[workorder.CLASE_TRABAJO] ?? woTypesBorders["DEFAULT"],
    crewId: normalizeCrewId(workorder.AMCREW),
    description: workorder.DESCRIPTION,
    disabled: [0, 6],
    duration: workorder.DURACION,
    durationText: parseHoursToText(parseFloat(workorder.DURACION)),
    enabledDays: [...enabledDays],
    end: parseDateAsUTC(endTmp),
    endsAfterWeek:
      isAfterWeek(end, start) ||
      getWorkorderAfterWeekPlanlabor(workorder.WORKORDERID)?.length > 0,
    entrega: workorder.ENTREGA,
    expirationDate: workorder.CADUCIDAD,
    limitDate: workorder.LIMITDATE,

    h: 1,
    i: workorder.WONUM,
    internalHours:
      workorder.WORKORDEREXTRA?.INTERNAL_HOURS ?? workorder.INTERNAL_HOURS,
    isDragging: false,
    location: masters.getLocationCodeAndName(
      workorder.SITEID + "-" + workorder.LOCATION
    ),
    locationId: workorder.LOCATION,

    locationName: masters.getLocationName(
      workorder.SITEID + "-" + workorder.LOCATION
    ),
    matPlanificados: workorder.MATPLANIFICADOS,
    mother_wo_description: workorder.DESCOTMADRE,
    motherWonum: workorder.OTMADRE,
    orgid: workorder.ORGID,
    otAReprogramar: workorder.OTAREPROGRAMAR,
    personGroup: workorder.PERSONGROUP,
    criticality: workorder.CRITICIDAD,
    legal: workorder.LEGAL,
    supplyChainDate: workorder.NEWREQUIREDATE,
    targetDate: workorder.PMNEXTDATE,
    windMax: workorder.WINDMAX,
    windMin: workorder.WINDMIN,
    reliabilityDegree: workorder.RELIABILITYDEGREE,

    qsew: workorder.QSEW,
    regular: workorder.FLG_NOTREGULAR,
    schedStart: parseDateAsUTC(start),
    semaforo: workorder.SEMAFORO,
    siteid: workorder.SITEID,

    start: parseDateAsUTC(start),
    startsBeforeWeek: startsBefore(start, workorder.WORKORDERID),
    status: workorder.STATUS,
    subcontractor: "",
    technicians: workorder.PLANLABOR ? workorder.PLANLABOR.split("#") : [],
    tieneOTsHijas: workorder.TIENEOTSHIJAS,
    timeWorked: workorder.ACTLABHRS,
    w: size,
    wonum: workorder.WONUM,
    workedHours,
    workedRemainingHours,
    workorderid: workorder.WORKORDERID,
    x: getDateDay(parseDateAsUTC(start)),
    y: positionY,
  };
};

const isBeforeWeek = (start, schedStart) => {
  return (
    parseDateAsUTC(start) < parseDateAsUTC(schedStart) &&
    getWeek(parseDateAsUTC(start)) !== getWeek(parseDateAsUTC(schedStart))
  );
};

const isAfterWeek = (end, schedStart) => {
  return (
    parseDateAsUTC(end) > parseDateAsUTC(schedStart) &&
    getWeek(parseDateAsUTC(end)) !== getWeek(parseDateAsUTC(schedStart))
  );
};

/**
 * Devuelve la condición de si una OT empieza antes de la semana de navegación
 * Si
 * @param {Date} startDate
 * @param {String} workorderid
 * @returns
 */
const startsBefore = (startDate, workorderid) => {
  const hasPlanlabor = getWorkorderWeekPlanlabor(workorderid);
  if (hasPlanlabor) {
    return getWorkorderBeforeWeekPlanlabor(workorderid)?.length > 0;
  }

  return isBeforeWeek(startDate, startDate);
};

/**
 * Ordena las OTs con su estructura original por Fecha y WONUM
 * @param {Array} workorders
 * @returns
 */
const sortPlanningWorkorders = (workorders) => {
  return workorders.sort((a, b) => {
    // Comparar por FECHA_PROGRAMADA
    const dateComparison =
      parseDateAsUTC(a.FECHA_PROGRAMADA) - parseDateAsUTC(b.FECHA_PROGRAMADA);

    if (dateComparison !== 0) {
      return dateComparison; // Si las fechas son diferentes, retorna la comparación
    }

    // Si las fechas son iguales, comparar por WONUM
    if (a.WONUM > b.WONUM) {
      return 1;
    } else if (a.WONUM < b.WONUM) {
      return -1;
    }

    return 0; // Si ambos valores (fecha y WONUM) son iguales
  });
};

/**
 * Ordena las OTs del algoritmo con su estructura original por Fecha y WONUM
 * @param {Array} workorders
 * @returns
 */
const sortAlgorithmWorkorders = (workorders) => {
  return workorders.sort((a, b) => {
    // Comparar por FECHA_PROGRAMADA
    const dateComparison =
      parseDateAsUTC(a.OPT_SCHEDSTART) - parseDateAsUTC(b.OPT_SCHEDSTART);

    if (dateComparison !== 0) {
      return dateComparison; // Si las fechas son diferentes, retorna la comparación
    }

    const a_wonum = a.COD_WONUM_ID.split("-")[1];
    const b_wonum = b.COD_WONUM_ID.split("-")[1];
    // Si las fechas son iguales, comparar por WONUM
    if (a_wonum > b_wonum) {
      return 1;
    } else if (a_wonum < b_wonum) {
      return -1;
    }

    return 0; // Si ambos valores (fecha y WONUM) son iguales
  });
};

/**
 * Escritura en Maximo y petición de la OT modificada
 * @param {Object} ot //El valor de DragPos
 * @param {"full"|"quick"|"drag"} editionType - Tipo de edición:
 *        "full" = edición completa,
 *        "quick" = acciones rápidas,
 *        "drag" = replanificación por arrastre.
 * @returns {Object} new_ot
 */
export const modifyWorkorder = async (
  ot,
  editionType = WO_EDITION_TYPE.FULL
) => {

  const nuxtApp = useNuxtApp();

  const new_wo_data = buildWorkOrderData(ot, editionType);

  const data = await fetchPostData(
    WWSS.scheduling.workorder.modify,
    new_wo_data
  ).catch((reason) => {
    console.error(nuxtApp.$i18n.t("errors.messages.backend.modifyWO"), reason);
  });

  if (data && data.error) {
    console.error(
      nuxtApp.$i18n.t("errors.messages.backend.modifyWO"),
      data.error
    );
    return { ...data.error, error: true };
  }

  return await getWOData(ot.workorderid);
};

export const modifyChildWorkorderParent = async (
  childWorkorder,
  parentWonum = ""
) => {
  const nuxtApp = useNuxtApp();
  const userStore = useUserStore();

  const params = {
    WORKORDERID: childWorkorder.WORKORDERID ?? childWorkorder.workorderid,
    WONUM: childWorkorder.WONUM ?? childWorkorder.wonum,
    PARENT: parentWonum,
    LANG:
      nuxtApp.$i18n.locale.value === FRONTEND_LOCALES.EN
        ? BACKEND_LOCALES.EN
        : BACKEND_LOCALES.ES,
    INTCHANGEBY: userStore?.login?.CodOperarioMaximo,
    INTSENDERSYS: SYSTEM,
  };

  const data = await fetchPostData(
    WWSS.scheduling.workorder.modify,
    params
  ).catch((reason) => {
    console.error(
      nuxtApp.$i18n.t("errors.messages.backend.modifyWO"),
      reason
    );
    return { error: { message: reason } };
  });

  if (data?.error) {
    const message =
      data.error.message || data.message || String(data.error || "");

    return {
      error: true,
      message,
      workorder: childWorkorder,
    };
  }

  return {
    error: false,
    workorder: childWorkorder,
  };
};

/**
 * Escritura en Maximo y petición de la OT modificada
 * @param {Object} params //El valor de  la OT en edición
 * @param {Boolean} updateData - Pide la info de la OT actualizada
 * @returns {Object} new_ot
 */
export const rescheduleWorkorder = async (params, updateData = false) => {

  const nuxtApp = useNuxtApp();

  const data = await fetchPostData(
    WWSS.scheduling.workorder.modify_extra,
    params
  ).catch((reason) => {
    console.error(
      nuxtApp.$i18n.t("errors.messages.backend.modifyExtraWO"),
      reason
    );
  });

  if (data && data.error) {
    console.error(
      nuxtApp.$i18n.t("errors.messages.backend.modifyExtraWO"),
      data.error
    );
    return null;
  }

  if (updateData) {
    return await getWOData(params.WORKORDERID);
  }
};

/**
 * Construye los datos para actualizar un trabajo.
 *
 * @param {Object} ot - Objeto con información del trabajo.
 * @param {String} editionType - Tipo de edición:
 * @returns {Object} Objeto listo para enviar al backend.
 */
const buildWorkOrderData = (ot, editionType) => {
  const nuxtApp = useNuxtApp();
  const userStore = useUserStore();
  const scheduledStart = ot.actualSchedStart ?? ot.start;

  const baseData = {
    AMCREW: ot.crewId === CREW_NULL ? "" : ot.crewId,
    INTCHANGEBY: userStore?.login?.CodOperarioMaximo,
    INTSENDERSYS: SYSTEM,
    LANG:
      nuxtApp.$i18n.locale.value === FRONTEND_LOCALES.EN
        ? BACKEND_LOCALES.EN
        : BACKEND_LOCALES.ES,
    SCHEDSTART: formatDate(scheduledStart, "DMY", "/"),
    WONUM: ot.wonum,
    WORKORDERID: ot.workorderid,
  };

  // edición completa
  const fullEditionData =
    editionType === WO_EDITION_TYPE.ALGORITHM
      ? {
          // LEAD: ot.entrega || "",
          // PARENT: ot.motherWonum || "",
          // PERSONGROUP: ot.personGroup || "",
          QSEW: ot.qsew,
        }
      : {
          LEAD: ot.entrega || "",
          PARENT: ot.motherWonum || "",
          PERSONGROUP: ot.personGroup || "",
          QSEW: ot.qsew,
        };

  // edición parcial (acciones rápidas)
  const quickActionsData = {
    LEAD: ot.entrega || "",
    PERSONGROUP: ot.personGroup || "",
    QSEW: ot.qsew,
  };

  let new_wo_data = { ...baseData };

  if (editionType === "full" || editionType === WO_EDITION_TYPE.ALGORITHM) {
    Object.assign(new_wo_data, fullEditionData);
  } else if (editionType === "quick") {
    Object.assign(new_wo_data, quickActionsData);
  }

  return new_wo_data;
};

/**
 * Obtener la planificación de una OT
 * @param {String} workorderid
 * @returns {Object}
 */
export const getWOData = async (workorderid) => {
  const ot_structure = await fetchPostData(WWSS.scheduling.workorder.get_ot, {
    workorderid,
  }).catch((reason) => {
    console.error(
      nuxtApp.$i18n.t("errors.messages.backend.modifiedGetWO"),
      reason
    );
  });

  if (ot_structure && ot_structure.error) {
    console.error(
      nuxtApp.$i18n.t("errors.messages.backend.modifiedGetWO"),
      ot_structure.error
    );
    return null;
  }
  return ot_structure;
};

/**
 * Obtención del Workorderid de una OT del algoritmo
 * @param {String} siteid
 * @param {String} wonum
 * @returns
 */
export const getWOWorkorderid = async (siteid, wonum) => {
  const nuxtApp = useNuxtApp();
  const workorderid = await fetchPostData(
    WWSS.scheduling.workorder.get_ot_workorderid,
    {
      siteid,
      wonum,
    }
  ).catch((reason) => {
    console.error(
      nuxtApp.$i18n.t("errors.messages.backend.getWorkorderid"),
      reason
    );
  });

  if (workorderid && workorderid.error) {
    console.error(
      nuxtApp.$i18n.t("errors.messages.backend.getWorkorderid"),
      workorderid.error
    );
    return null;
  }

  if (!workorderid.length) {
    return "";
  }

  return workorderid[0].WORKORDERID;
};

/**
 * @param {Array} refGrid
 */
export const updateReferences = (refGrid) => {
  const domGrids = [...document.querySelectorAll(".draggable")]; // Los elementos DOM actuales

  // Reordenar refGrid basado en el orden de domGrids en el DOM
  refGrid = refGrid.sort((a, b) => {
    const indexA = domGrids.indexOf(a.$el); // Posición en el DOM
    const indexB = domGrids.indexOf(b.$el);
    return indexA - indexB;
  });

  // Recorro todos los contenedores para intentar corregir el NO pintado de la cuadrilla en el arrastre en cuadrillas intermedias desde origen a destino una vez actualizado.
  for (const layout of refGrid) {
    layout.$nextTick();
    layout.layoutUpdate();
  }
  return refGrid;
};

/**
 * Hace el scroll y resetea los valores
 * @param {Object} item  //DragPos.value || OriginalWO.value
 */
export const scrollToWO = (wo) => {
  setTimeout(() => {
    const element = document.getElementById(`${wo.crewId}#${wo.wonum}`);

    if (element) {
      const elementPosition =
        element.getBoundingClientRect().top + window.scrollY - 220;
      window.scrollTo({ top: elementPosition, behavior: "smooth" });
    }
  }, 600);
};

/**
 * Función de scroll a la cuadrilla
 * @param {String} crew_id
 */
export const scrollToCrew = (crew_id) => {
  const globalScheduling = useGlobalScheduling();
  setTimeout(() => {
    const element = document.getElementById(`draggable_${crew_id}`);
    if (element) {
      const elementPosition =
        element.getBoundingClientRect().top + window.scrollY - 145;
      window.scrollTo({ top: elementPosition, behavior: "smooth" });
    }
  }, 600);

  setTimeout(() => {
    globalScheduling.clearDraggingCardWO();
  }, 1400);
};

export const getSemaforoColor = (semaforo) => {
  const colors = {
    1: "red-accent-4",
    2: "yellow-accent-4",
    3: "light-green-darken-3",
  };

  return colors[semaforo];
};

export const workTypeLabel = (workType) => {
  const nuxtApp = useNuxtApp();
  const masters = useMasters();
  // Verifica que masters.workTypes.map, props.workType y props.locale estén definidos
  const workTypeData = masters.workTypes?.map?.[workType];
  const locale = nuxtApp.$i18n.locale.value;

  if (workTypeData && locale) {
    return workTypeData[`DESCRIPTION_${locale.toUpperCase()}`] || "";
  }
  // Si falta algún dato, devuelve una cadena vacía o un valor por defecto
  return "";
};

export const parseWorkorderRaw = (workorder) => {
  const masters = useMasters();
  const nuxtApp = useNuxtApp();
  return {
    actualSchedStart: parseDateAsUTC(workorder.FECHA_PROGRAMADA),
    binmea: workorder.BINMEA,
    claseTrabajo: workorder.CLASE_TRABAJO,
    color: woTypesColors[workorder.CLASE_TRABAJO],
    crewId: normalizeCrewId(workorder.AMCREW),
    description: workorder.DESCRIPTION,
    duration: workorder.DURACION,
    durationText: parseHoursToText(parseFloat(workorder.DURACION)),
    end: null,
    entrega: workorder.ENTREGA,
    expirationDate: workorder.CADUCIDAD,
    limitDate: workorder.LIMITDATE,
    i: workorder.WONUM,
    internalHours:
      workorder.WORKORDEREXTRA?.INTERNAL_HOURS ?? workorder.INTERNAL_HOURS,
    location: masters.getLocationCodeAndName(
      workorder.SITEID + "-" + workorder.LOCATION
    ),
    locationId: workorder.LOCATION,
    locationName: masters.getLocationName(
      workorder.SITEID + "-" + workorder.LOCATION
    ),
    matPlanificados: workorder.MATPLANIFICADOS,
    mother_wo_description: workorder.DESCOTMADRE,
    motherWonum: workorder.OTMADRE,
    orgid: workorder.ORGID,
    otAReprogramar: workorder.OTAREPROGRAMAR,
    personGroup: workorder.PERSONGROUP,
    criticality: workorder.CRITICIDAD,
    legal: workorder.LEGAL,
    supplyChainDate: workorder.NEWREQUIREDATE,
    targetDate: workorder.PMNEXTDATE,
    windMax: workorder.WINDMAX,
    windMin: workorder.WINDMIN,
    reliabilityDegree: workorder.RELIABILITYDEGREE,
    qsew: workorder.QSEW,
    schedStart: parseDateAsUTC(workorder.FECHA_PROGRAMADA),
    semaforo: workorder.SEMAFORO,
    siteid: workorder.SITEID,
    start: parseDateAsUTC(workorder.FECHA_PROGRAMADA),
    status: workorder.STATUS,
    technicians: workorder?.PLANLABOR ? workorder.PLANLABOR.split("#") : [],
    tieneOTsHijas: workorder.TIENEOTSHIJAS ?? "0",
    timeWorked: workorder.ACTLABHRS,
    wonum: workorder.WONUM,
    workorderid: workorder.WORKORDERID,
  };
};
